import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import TypeBadge from '../components/TypeBadge';
import './Moves.css';

function Moves() {
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const lastMoveElementRef = useRef();
  const scrollContainerRef = useRef();

  const loadMoves = useCallback(async (pageNum, search = '', shouldReset = false) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/moves?page=${pageNum}&limit=20&search=${search}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch moves');
      }
      const responseData = await response.json();
      const { data } = responseData;
      
      setMoves(prev => shouldReset ? data.moves : [...prev, ...data.moves]);
      setHasMore(data.pagination.hasNextPage);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadMoves(1, '', true);
    const currentObserverRef = observer.current;
    return () => {
      if (currentObserverRef) {
        currentObserverRef.disconnect();
      }
    };
  }, [loadMoves]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        setPage(1);
        loadMoves(1, searchTerm, true);
        // Reset scroll position on search
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadMoves]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    const options = {
      root: scrollContainerRef.current,
      rootMargin: '20px',
      threshold: 0.1,
    };

    const handleObserver = (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        loadMoves(nextPage, searchTerm, false);
      }
    };

    const currentObserver = new IntersectionObserver(handleObserver, options);
    const lastMoveElement = lastMoveElementRef.current;

    if (lastMoveElement) {
      currentObserver.observe(lastMoveElement);
    }

    return () => {
      if (lastMoveElement) {
        currentObserver.unobserve(lastMoveElement);
      }
    };
  }, [loading, hasMore, page, searchTerm, loadMoves]);

  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="moves-page-container">
      <h2>Pokémon Moves</h2>
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search moves by name or type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div 
        ref={scrollContainerRef}
        className="moves-content-container"
      >
        <div className="moves-grid">
          {moves.map((move, index) => (
            <Link 
              ref={index === moves.length - 1 ? lastMoveElementRef : null}
              to={`/moves/${move.name}`} 
              key={move.name} 
              className="move-card"
            >
              <div className="move-header">
                <h3>{move.name}</h3>
                <TypeBadge type={move.type} />
              </div>
              <div className="move-stats">
                <div className="stat">
                  <span>Power</span>
                  <span>{move.power || '-'}</span>
                </div>
                <div className="stat">
                  <span>Accuracy</span>
                  <span>{move.accuracy ? `${move.accuracy}%` : '-'}</span>
                </div>
                <div className="stat">
                  <span>Category</span>
                  <span>{move.category}</span>
                </div>
              </div>
              <p className="move-effect">{move.shortEffect}</p>
            </Link>
          ))}
        </div>
        {loading && <div className="loading">Loading moves...</div>}
      </div>
    </div>
  );
}

export default Moves; 